import { useEffect, useRef, useMemo } from 'react';
import Trans from 'next-translate/Trans';
// Hooks
import useOnboardingTasks from '@/utils/hooks/swr/useOnboardingTasks';
import useSocialProfile from '@/utils/hooks/swr/useSocialProfile';
import useActiveSocialProfile from '@/utils/hooks/user/useActiveSocialProfile';

// Lib
import { onboardingTasks } from '@/lib/onboardingTasks/tasks';
import { firstStatsTasks } from '@/lib/firstStats/firstStatsTasks';
import { FIRST_STATS_STATUS } from '@/lib/firstStats/firstStatsStatus';

// Components
import InfoBanner from '@/components/base/InfoBanner/CompoundBanner';
import Layout from '@/components/composite/FirstStats/Layout';
import Header from '@/components/composite/FirstStats/Content/Header';
import AvailableTasksList from '@/components/composite/FirstStats/Content/AvailableTasksList';
import RelaunchButtonBlock from '@/components/composite/FirstStats/Content/RelaunchButtonBlock';
import ProgressBarBlock from '@/components/composite/FirstStats/Content/ProgressBarBlock';
import TasksSummaryList from '@/components/composite/FirstStats/Content/TasksSummaryList';
import StartButtonBlock from '@/components/composite/FirstStats/Content/StartButtonBlock';

// Types
import type { TaskType } from '@/lib/onboardingTasks/tasks';
import type { TaskType as OnboardingTaskType } from '@/utils/hooks/swr/useOnboardingTasks';
import type { FirstStatsTaskType } from '@/lib/firstStats/firstStatsTasks';
import type { StartButtonBlockProps } from '@/components/composite/FirstStats/Content/StartButtonBlock';
import type { FirstStatsStatusType } from '@/lib/firstStats/firstStatsStatus';

export type AvailableTaskType = TaskType & OnboardingTaskType & FirstStatsTaskType;

export type ContentProps = Pick<StartButtonBlockProps, 'start'>;

const Content = ({ start }: ContentProps) => {
    const { tasks, mutateTasks, tasksError } = useOnboardingTasks({
        shouldRetryOnError: false,
        revalidateOnFocus: false,
        revalidateOnReconnect: false
    });

    const { activeSocialProfile } = useActiveSocialProfile();

    const { socialProfile, mutateSocialProfile } = useSocialProfile({
        SPID: activeSocialProfile?.id
    });

    const availableTasks: AvailableTaskType[] = useMemo(
        () =>
            Object.keys(firstStatsTasks).map((taskSlug) => ({
                ...onboardingTasks[taskSlug],
                ...firstStatsTasks[taskSlug],
                ...tasks?.find((task) => task.slug === taskSlug)
            })),
        [tasks]
    );

    const completedTasksCount = useMemo(
        () => availableTasks.filter((task) => task.completed_at).length,
        [availableTasks]
    );

    const firstStatsStatus: FirstStatsStatusType = useMemo(() => {
        if (
            activeSocialProfile?.first_stats ||
            activeSocialProfile?.full_stats ||
            socialProfile?.first_stats ||
            socialProfile?.full_stats
        ) {
            return FIRST_STATS_STATUS.READY;
        }

        if (activeSocialProfile?.first_stats_relaunch || socialProfile?.first_stats_relaunch) {
            return FIRST_STATS_STATUS.RELAUNCH;
        }

        if (
            activeSocialProfile?.first_stats_relaunch_failed ||
            socialProfile?.first_stats_relaunch_failed
        ) {
            return FIRST_STATS_STATUS.RELAUNCH_FAILED;
        }

        return FIRST_STATS_STATUS.LOADING;
    }, [activeSocialProfile, socialProfile]);

    const timeoutId = useRef<NodeJS.Timeout>();

    useEffect(() => {
        clearTimeout(timeoutId.current);
        if (socialProfile && !socialProfile.first_stats && !socialProfile?.full_stats) {
            timeoutId.current = setTimeout(() => {
                mutateSocialProfile();
            }, 30000);
        }

        return () => clearTimeout(timeoutId.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [socialProfile]);

    return (
        <Layout className="h-full w-full">
            <Layout.MainContent>
                <Header
                    firstStatsStatus={firstStatsStatus}
                    completedTasksCount={completedTasksCount}
                />
                {firstStatsStatus === FIRST_STATS_STATUS.RELAUNCH ? (
                    <RelaunchButtonBlock
                        socialProfile={socialProfile}
                        onRelaunch={mutateSocialProfile}
                    />
                ) : null}
                {firstStatsStatus !== FIRST_STATS_STATUS.RELAUNCH &&
                firstStatsStatus !== FIRST_STATS_STATUS.RELAUNCH_FAILED &&
                tasksError ? (
                    <InfoBanner className="mb-4">
                        <InfoBanner.Icon />
                        <InfoBanner.Content
                            message={
                                <Trans
                                    i18nKey="commons:modal_first_stats_status.error.loading_onboarding_tasks"
                                    components={[
                                        <button
                                            key="link"
                                            onClick={() => {
                                                mutateTasks();
                                            }}
                                            className="font-GSemiBold underline"
                                        />
                                    ]}
                                />
                            }
                        />
                    </InfoBanner>
                ) : null}
                {firstStatsStatus !== FIRST_STATS_STATUS.RELAUNCH &&
                firstStatsStatus !== FIRST_STATS_STATUS.RELAUNCH_FAILED &&
                !tasksError ? (
                    <AvailableTasksList tasks={availableTasks} />
                ) : null}
            </Layout.MainContent>
            <Layout.SideContent>
                <div className="flex flex-col space-y-10">
                    {!tasksError ? (
                        <>
                            <ProgressBarBlock
                                tasksCount={availableTasks?.length}
                                completedTasksCount={completedTasksCount}
                            />
                            <TasksSummaryList tasks={availableTasks} />
                        </>
                    ) : null}
                    <StartButtonBlock firstStatsStatus={firstStatsStatus} start={start} />
                </div>
            </Layout.SideContent>
        </Layout>
    );
};

export default Content;
